<template>
  <div v-if="adToShow || googleAdStatus !== 'unfilled'" class="wrapper">
    <div class="with-title">
      <div class="title">
        <BaseText class="light-70">{{ $t("app.advertisement") }}</BaseText>
      </div>
      <div class="content">
        <AdvertisementHTML
          v-if="adToShow && adToShow.__typename === 'AdsAdvertisementHtmlEmbed'"
          :ad="adToShow"
          :is-fallback="googleAdStatus === 'unfilled'"
        />
        <AdvertisementImage
          v-else-if="adToShow && adToShow.__typename === 'AdsAdvertisementImage'"
          :ad="adToShow"
          :is-fallback="googleAdStatus === 'unfilled'"
        />
        <AdvertisementGoogle
          v-if="!adToShow"
          :display-at="displayAt"
          @update-ad-status="handleGoogleAdLoad"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { AdDisplayAt } from "~/graphql/generated";
import { AdTargetType, useAdsAdvertisementsQuery } from "~/graphql/generated";

const props = defineProps<{
  displayAt: AdDisplayAt;
}>();

const emit = defineEmits<(e: "ad-loaded") => void>();

const googleAdStatus = ref<"filled" | "unfilled" | null>(null);

// Generates a random number based on the 'displayAt' prop, so that each time the page is loaded a new random number is generated.
// We use asyncData so that the server and client both use the same random number and there won't be a hydration mismatch.
const { data: randomNumber } = await useAsyncData<number>(
  props.displayAt,
  () => new Promise((resolve) => resolve(Math.random())),
);

const shouldPauseAdsQuery = computed(() => !randomNumber.value);

const { data } = await useAdsAdvertisementsQuery({
  variables: {},
  requestPolicy: "cache-first",
  pause: shouldPauseAdsQuery,
});

const activeShopId = inject<ComputedRef<string | undefined> | undefined>("brochureData");

const adToShow = computed(() => {
    const defaultAds =
    data.value?.adsAdvertisements.filter((ad) => {
      // Filter out fallback ads as long as we don't know if Google can't fill it
      if (googleAdStatus.value !== "unfilled" && ad.targetType === AdTargetType.Fallback) return false;

      // Only show ads that need to be displayed at current ad position
      if (ad.displayAt !== props.displayAt) return false;

      // When viewing a brochure, only show ads that target this shop
      // (ads without any shop targeting are shown everywhere).
      if (activeShopId?.value && ad.shops?.length) {
        return ad.shops.some((s) => s?.id === activeShopId.value);
      }

      // We have a correct position and a generic ad, show it!
      return true;
    }) ?? [];

  if (defaultAds.length === 0) return null;

  // Return an ad based on the randomNumber and the weight of the ads
  const totalWeight = defaultAds.reduce((sum, ad) => sum + ad.weight, 0);
  let randomNum = (randomNumber.value ?? Math.random()) * totalWeight;
  let selectedAd = null;
  for (const ad of defaultAds) {
    randomNum -= ad.weight;
    if (randomNum <= 0) {
      selectedAd = ad;
      break;
    }
  }

  // Fallback selects the ad with the highest weight if none is selected somehow
  if (!selectedAd) {
    selectedAd = defaultAds.reduce((prevAd, currentAd) => {
      return currentAd.weight > prevAd.weight ? currentAd : prevAd;
    });
  }

  emit("ad-loaded");
  return selectedAd;
});

const handleGoogleAdLoad = (status: "filled" | "unfilled") => {
  if (googleAdStatus.value === "filled") return;
  googleAdStatus.value = status;
  if (status === "filled") emit("ad-loaded");
};
</script>

<style lang="scss" scoped>
.with-title {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.title {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 0.25rem;
  width: 100%;

  .text {
    padding: 0 1rem;
  }

  &::before,
  &::after {
    content: "";
    background-color: $light-80;
    width: 100%;
    height: 1px;
  }
}

.wrapper {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}
.content {
  position: relative;
  min-height: fit-content;
}
.hidden {
  position: absolute;
  top: 0;
  left: -50%;
  z-index: -1;
  opacity: 0;
}
</style>
